import type { Config } from "tailwindcss";
import tailwindcssAnimate from "tailwindcss-animate";

export default {
	darkMode: ["class"],
	content: [
		"./pages/**/*.{ts,tsx}",
		"./components/**/*.{ts,tsx}",
		"./app/**/*.{ts,tsx}",
		"./src/**/*.{ts,tsx}",
	],
	prefix: "",
	theme: {
		container: {
			center: true,
			padding: '2rem',
			screens: {
				'2xl': '1400px'
			}
		},
		extend: {
			fontFamily: {
				sans: ['Outfit Variable', 'Inter Variable', 'system-ui', 'sans-serif'],
			},
			colors: {
				border: 'hsl(var(--border))',
				input: 'hsl(var(--input))',
				ring: 'hsl(var(--ring))',
				background: 'hsl(var(--background))',
				foreground: 'hsl(var(--foreground))',
				primary: {
					DEFAULT: 'hsl(var(--primary))',
					foreground: 'hsl(var(--primary-foreground))'
				},
				secondary: {
					DEFAULT: 'hsl(var(--secondary))',
					foreground: 'hsl(var(--secondary-foreground))'
				},
				destructive: {
					DEFAULT: 'hsl(var(--destructive))',
					foreground: 'hsl(var(--destructive-foreground))'
				},
				muted: {
					DEFAULT: 'hsl(var(--muted))',
					foreground: 'hsl(var(--muted-foreground))'
				},
				accent: {
					DEFAULT: 'hsl(var(--accent))',
					foreground: 'hsl(var(--accent-foreground))'
				},
				popover: {
					DEFAULT: 'hsl(var(--popover))',
					foreground: 'hsl(var(--popover-foreground))'
				},
				card: {
					DEFAULT: 'hsl(var(--card))',
					foreground: 'hsl(var(--card-foreground))'
				},
				sidebar: {
					DEFAULT: 'hsl(var(--sidebar-background))',
					foreground: 'hsl(var(--sidebar-foreground))',
					primary: 'hsl(var(--sidebar-primary))',
					'primary-foreground': 'hsl(var(--sidebar-primary-foreground))',
					accent: 'hsl(var(--sidebar-accent))',
					'accent-foreground': 'hsl(var(--sidebar-accent-foreground))',
					border: 'hsl(var(--sidebar-border))',
					ring: 'hsl(var(--sidebar-ring))'
				},
				// Custom orange palette
				orange: {
					50: '#fff7ed',
					100: '#ffedd5',
					200: '#fed7aa',
					300: '#fdba74',
					400: '#fb923c',
					500: '#f97316',
					600: '#ea580c',
					700: '#c2410c',
					800: '#9a3412',
					900: '#7c2d12',
					950: '#431407',
				},
				ember: {
					DEFAULT: '#ff6b00',
					light: '#ff8c00',
					dark: '#cc5500',
				}
			},
			borderRadius: {
				lg: 'var(--radius)',
				md: 'calc(var(--radius) - 2px)',
				sm: 'calc(var(--radius) - 4px)'
			},
			backgroundImage: {
				'gradient-radial': 'radial-gradient(var(--tw-gradient-stops))',
				'gradient-fire': 'linear-gradient(135deg, #ff6b00 0%, #ff8c00 25%, #ff4500 50%, #ff6b00 75%, #ff8c00 100%)',
				'gradient-ember': 'linear-gradient(180deg, #ff8c00 0%, #ff6b00 50%, #cc5500 100%)',
				'gradient-premium': 'linear-gradient(135deg, #ff8c00 0%, #ff6b00 50%, #e65c00 100%)',
				'gradient-dark-fire': 'linear-gradient(135deg, rgba(255,107,0,0.3) 0%, rgba(255,140,0,0.2) 50%, rgba(204,85,0,0.3) 100%)',
			},
			keyframes: {
				'accordion-down': {
					from: { height: '0' },
					to: { height: 'var(--radix-accordion-content-height)' }
				},
				'accordion-up': {
					from: { height: 'var(--radix-accordion-content-height)' },
					to: { height: '0' }
				},
				'vote-pulse': {
					'0%, 100%': { transform: 'scale(1)', opacity: '1' },
					'50%': { transform: 'scale(1.08)', opacity: '0.9' }
				},
				'shimmer': {
					'0%': { backgroundPosition: '-200% 0' },
					'100%': { backgroundPosition: '200% 0' }
				},
				'slide-up': {
					'0%': { transform: 'translateY(20px)', opacity: '0' },
					'100%': { transform: 'translateY(0)', opacity: '1' }
				},
				'slide-up-bold': {
					'0%': { transform: 'translateY(40px) scale(0.95)', opacity: '0' },
					'100%': { transform: 'translateY(0) scale(1)', opacity: '1' }
				},
				'fade-in': {
					'0%': { opacity: '0' },
					'100%': { opacity: '1' }
				},
				'scale-in': {
					'0%': { transform: 'scale(0.9)', opacity: '0' },
					'100%': { transform: 'scale(1)', opacity: '1' }
				},
				'scale-in-bounce': {
					'0%': { transform: 'scale(0.5)', opacity: '0' },
					'60%': { transform: 'scale(1.1)', opacity: '1' },
					'100%': { transform: 'scale(1)', opacity: '1' }
				},
				'float': {
					'0%, 100%': { transform: 'translateY(0px)' },
					'50%': { transform: 'translateY(-20px)' }
				},
				'float-slow': {
					'0%, 100%': { transform: 'translateY(0px) scale(1)' },
					'50%': { transform: 'translateY(-30px) scale(1.05)' }
				},
				'glow': {
					'0%, 100%': { boxShadow: '0 0 20px rgba(255, 140, 0, 0.4)' },
					'50%': { boxShadow: '0 0 50px rgba(255, 140, 0, 0.8)' }
				},
				'glow-pulse': {
					'0%, 100%': { boxShadow: '0 0 20px rgba(255, 140, 0, 0.3), 0 0 40px rgba(255, 140, 0, 0.1)' },
					'50%': { boxShadow: '0 0 40px rgba(255, 140, 0, 0.6), 0 0 80px rgba(255, 140, 0, 0.3)' }
				},
				'bounce-in': {
					'0%': { transform: 'scale(0.3)', opacity: '0' },
					'50%': { transform: 'scale(1.08)' },
					'70%': { transform: 'scale(0.95)' },
					'100%': { transform: 'scale(1)', opacity: '1' }
				},
				'fire-dance': {
					'0%, 100%': { transform: 'scaleY(1) translateX(0)' },
					'25%': { transform: 'scaleY(1.1) translateX(-2px)' },
					'50%': { transform: 'scaleY(0.95) translateX(2px)' },
					'75%': { transform: 'scaleY(1.05) translateX(-1px)' }
				},
				'ember-float': {
					'0%, 100%': { transform: 'translateY(0) translateX(0)', opacity: '0.5' },
					'50%': { transform: 'translateY(-25px) translateX(10px)', opacity: '1' }
				},
				'progress-fill': {
					'0%': { width: '0%', opacity: '0.5' },
					'100%': { opacity: '1' }
				},
				'count-up': {
					'0%': { transform: 'translateY(20px) scale(0.5)', opacity: '0' },
					'50%': { transform: 'translateY(-5px) scale(1.15)' },
					'100%': { transform: 'translateY(0) scale(1)', opacity: '1' }
				},
				'vote-ripple': {
					'0%': { transform: 'scale(0.8)', opacity: '1' },
					'100%': { transform: 'scale(2.5)', opacity: '0' }
				},
				'pulse-ring': {
					'0%': { transform: 'scale(1)', opacity: '1' },
					'100%': { transform: 'scale(1.4)', opacity: '0' }
				},
				'text-shimmer': {
					'0%': { backgroundPosition: '-200% center' },
					'100%': { backgroundPosition: '200% center' }
				},
				'wiggle': {
					'0%, 100%': { transform: 'rotate(-2deg)' },
					'50%': { transform: 'rotate(2deg)' }
				},
				'breathe': {
					'0%, 100%': { transform: 'scale(1)', opacity: '0.7' },
					'50%': { transform: 'scale(1.15)', opacity: '1' }
				},
				'spin-slow': {
					'0%': { transform: 'rotate(0deg)' },
					'100%': { transform: 'rotate(360deg)' }
				},
				'gradient-shift': {
					'0%': { backgroundPosition: '0% 50%' },
					'50%': { backgroundPosition: '100% 50%' },
					'100%': { backgroundPosition: '0% 50%' }
				},
				'shine': {
					'0%': { left: '-100%' },
					'50%, 100%': { left: '100%' }
				},
				'bounce-subtle': {
					'0%, 100%': { transform: 'translateY(0)' },
					'50%': { transform: 'translateY(-8px)' }
				},
				'heat-wave': {
					'0%, 100%': { filter: 'blur(0px)', transform: 'translateY(0)' },
					'50%': { filter: 'blur(0.5px)', transform: 'translateY(-2px)' }
				}
			},
			animation: {
				'accordion-down': 'accordion-down 0.2s ease-out',
				'accordion-up': 'accordion-up 0.2s ease-out',
				'vote-pulse': 'vote-pulse 0.6s ease-in-out',
				'shimmer': 'shimmer 2.5s linear infinite',
				'slide-up': 'slide-up 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
				'slide-up-bold': 'slide-up-bold 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
				'fade-in': 'fade-in 0.4s ease-out',
				'scale-in': 'scale-in 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
				'scale-in-bounce': 'scale-in-bounce 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
				'float': 'float 4s ease-in-out infinite',
				'float-slow': 'float-slow 6s ease-in-out infinite',
				'glow': 'glow 2.5s ease-in-out infinite',
				'glow-pulse': 'glow-pulse 3s ease-in-out infinite',
				'bounce-in': 'bounce-in 0.7s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
				'fire-dance': 'fire-dance 0.8s ease-in-out infinite',
				'ember-float': 'ember-float 4s ease-in-out infinite',
				'progress-fill': 'progress-fill 1s ease-out forwards',
				'count-up': 'count-up 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
				'vote-ripple': 'vote-ripple 0.8s ease-out',
				'pulse-ring': 'pulse-ring 1.5s ease-out infinite',
				'text-shimmer': 'text-shimmer 3s linear infinite',
				'wiggle': 'wiggle 0.3s ease-in-out',
				'breathe': 'breathe 4s ease-in-out infinite',
				'spin-slow': 'spin-slow 8s linear infinite',
				'gradient-shift': 'gradient-shift 4s ease infinite',
				'shine': 'shine 2s ease-in-out infinite',
				'bounce-subtle': 'bounce-subtle 2s ease-in-out infinite',
				'heat-wave': 'heat-wave 2s ease-in-out infinite'
			}
		}
	},
	plugins: [tailwindcssAnimate],
} satisfies Config;
